import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { ParkingTimeGrid } from './../../helpers/from-backend/parking-time-grid';
import { AfterViewChecked, ChangeDetectorRef, Component, Input, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { DropDownConstantsFactory } from '../../../../base/shared/syncfusion/dropdown-constants-factory';
import { ParkingHelper } from '../../parking-helper';
import { AuthService } from '../../../../base/services/auth.service';
import {
  AutomaticParkingScheduleDTO,
  ParkerRegistrationOffense,
  ParkingProcessSubscriptionInfoDTO,
  ParkingProcessTariffInfoDTO,
  ParkingProcessZoneInfoDTO,
  UserInfoDTO,
  WeekDayFlags,
} from 'src/app/base/models/models.swagger.generated';
import { ParkingService } from '../../parking/parking.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { distinctUntilChanged, filter, startWith, tap } from 'rxjs/operators';
import { combineLatest, Subscription } from 'rxjs';
import { DateHelper } from '../../../../base/shared/date-helper';
import { DateTime } from 'luxon';
import { BitHelper } from '../../../../base/shared/bit-helper';
import { WeekDayFlagsHelper } from 'src/app/base/shared/utils/week-day-flags.helper';
import { ParkingTariffValidityHelper } from '../../helpers/parking-tariff-validity.helper';
import { IDurationRange } from 'src/app/base/shared/time-picker-with-time-slider/time-picker-with-time-slider.component';
import { SessionTimeSlicer } from '../../helpers/from-backend/session-time-slicer';
import { YesNoGroupButtonComponent } from '../../../../base/shared/yes-no-group-button/yes-no-group-button.component';
import { TimePickerWithTimeSliderComponent } from '../../../../base/shared/time-picker-with-time-slider/time-picker-with-time-slider.component';
import { DaySelectorComponent } from '../../../../base/shared/day-selector/day-selector.component';
import { NgClass } from '@angular/common';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { AppErrorMessagesComponent } from '../../../../base/error-messages/app-error-messages.component';
import { AutomaticParkingScheduleEditFormGroup } from './parking-administration-edit-form.type';
import { DaySelectorFormGroup } from 'src/app/base/shared/day-selector/day-selector-form.type';
import { TimePickerWithTimeSliderFormGroup } from 'src/app/base/shared/time-picker-with-time-slider/time-picker-with-time-slider-form.type';

export interface ExtendedTariffValidityRange extends IDurationRange {
  startTimeStr?: string;
  endTimeStr?: string;
  possibleParkingEndTime?: string;
}

@Component({
    selector: 'app-parking-administration-edit-form',
    templateUrl: './parking-administration-edit-form.component.html',
    styleUrls: ['./parking-administration-edit-form.component.scss'],
    imports: [
        ReactiveFormsModule,
        AppErrorMessagesComponent,
        DropDownListModule,
        NgClass,
        DaySelectorComponent,
        TimePickerWithTimeSliderComponent,
        YesNoGroupButtonComponent,
        TranslateModule
    ]
})
export class ParkingAdministrationEditFormComponent implements OnInit, AfterViewChecked, OnDestroy {

  @Input() public parkingForm: AutomaticParkingScheduleEditFormGroup;

  public clientDropdownSettings = DropDownConstantsFactory.createStandardClientDropdownSettings();
  public maxDuration: number;
  public possibleStartTimeRanges: ExtendedTariffValidityRange[] = [];
  public get overlappingSchedule(): AutomaticParkingScheduleDTO {
    if(this.parkingForm.valid) {
      return null;
    }
    return this.parkingForm.getError('overlappingSchedule');
  }

  public get clientDropdownValues() {
    return this.parkingHelper.clientDropdownValues;
  }

  public get subscriptionsDropdownValues() {
    return this.parkingHelper.subscriptionsDropdownValues;
  }

  public get zoneDropdownValues() {
    return this.parkingHelper.zoneDropdownValues;
  }

  public get parkingSpaceNumberDefinition() {
    return this.parkingHelper.parkingSpaceNumberDefinition;
  }

  public get controlPlateDropdownValues() {
    return this.parkingHelper.controlPlateDropdownValues;
  };

  public get clientSelected(): boolean {
    return !!this.parkingForm.controls.clientId.value;
  }

  public get zoneSelected(): boolean {
    return !!this.parkingForm.controls.zoneId.value;
  }

  public get isSubscriptionSelected(): boolean {
    return !!this.parkingForm.controls.subscriptionAssignmentId.value;
  }

  public get isEdit(): boolean {
    return !!this.parkingForm.controls.id.value;
  }

  public get parkingProcessZoneInfoDTO(): ParkingProcessZoneInfoDTO[] {
    return this.parkingHelper.parkingProcessZoneInfoDTO;
  }

  public get hasPossibleStartTimeRanges(): boolean {
    return !!this.possibleStartTimeRanges?.length;
  }

  public get weekdaysFormGroup(): DaySelectorFormGroup {
    return this.parkingForm.controls.weekdays;
  }

  public get timePickerFormGroup(): UntypedFormGroup {
    return this.parkingForm as UntypedFormGroup;
  }

  private user: UserInfoDTO;
  private tariffs: ParkingProcessTariffInfoDTO[];
  private parkingHelper: ParkingHelper;
  private selectedSubcsription: ParkingProcessSubscriptionInfoDTO;

  private formSubscriptions: Subscription = new Subscription();
  private weekdayControlNames: string[];
  private timeGrid: ParkingTimeGrid;

  constructor(private authService: AuthService,
              private parkingService: ParkingService,
              private translate: TranslateService,
              private cdr: ChangeDetectorRef,
              spinnerService: SpinnerService) {
    this.parkingHelper = new ParkingHelper(true, this.parkingService, this.translate, spinnerService);
  }

  ngOnInit(): void {
    this.user = this.authService.getUserInfo();
    this.weekdayControlNames = Object.keys(this.parkingForm.getRawValue().weekdays);
    this.parkingHelper.initDropDownValuesForValidForm(this.user, this.parkingForm).subscribe(() => {
      this.assignFormFieldSubscriptions();
    });
  }

  public ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  public ngOnDestroy(): void {
    this.formSubscriptions?.unsubscribe();
  }

  public resetZone(event: any): void {
    if (event.isInteracted) {
      this.parkingHelper.resetZone(this.parkingForm);
    }
  }

  public resetSubscription(event: any): void {
    if (event.isInteracted) {
      this.parkingHelper.resetSubscription(this.parkingForm);
    }
  }

  public resetWeekdays(event: any): void {
    if (event.isInteracted) {
      this.parkingHelper.resetWeekdays(this.parkingForm);
    }
  }

  public onStartTimeChange(selectedTime: Date): void {
    if (selectedTime != null && this.possibleStartTimeRanges?.length) {
      const selectedTimeAsDateTime = DateTime.fromJSDate(selectedTime);
      let selectedStartTime = selectedTimeAsDateTime.diff(selectedTimeAsDateTime.startOf('day'));
      const pairForSelectedTime = this.possibleStartTimeRanges.find(pair => pair.startTime.toMillis() <= selectedStartTime.toMillis() &&
                                                                            pair.endTime.toMillis() > selectedStartTime.toMillis());
      if (!pairForSelectedTime) {
        // adjust start time
        const closestPairForSelectedTime = this.possibleStartTimeRanges.reduce((prev, curr) =>
          (Math.abs(curr.startTime.toMillis() - selectedStartTime.toMillis()) <
            Math.abs(prev.startTime.toMillis() - selectedStartTime.toMillis()) ? curr : prev));
        selectedStartTime = closestPairForSelectedTime.startTime;
        selectedTime = DateHelper.getDateTimeStart1970().plus(closestPairForSelectedTime.startTime).toJSDate();
        this.parkingForm.controls.startTime.setValue(selectedTime);
        this.parkingForm.controls.duration.setValue(null);
      }

      const weekDayFlags = WeekDayFlagsHelper.daysToFlag(this.parkingForm.controls.weekdays.value);
      if (this.timeGrid && weekDayFlags) {
        // calculate maxDuration
        let maxDuration = Number.MAX_SAFE_INTEGER;
        const baseStartDateTime = this.timeGrid.start.startOf('day').plus(selectedStartTime);
        for (let i = 0; i < 7; i++) {
          const startDateTime = baseStartDateTime.plus({days: i});
          if (!WeekDayFlagsHelper.hasFlags(weekDayFlags, WeekDayFlagsHelper.luxonWeekdayToFlag(startDateTime.weekday))) {
            continue;
          }
          const slicer = SessionTimeSlicer.for(this.timeGrid);
          const slices = slicer.slice(startDateTime, startDateTime.plus({hours: 24}));
          let actualMaxDuration = 0;
          if (slices && slices.length) {
            if (slices[0].offense === ParkerRegistrationOffense.NoOffense) {
              const start = slices[0].timeRange.startTime;
              let end = slices[slices.length - 1].timeRange.endTime;
              if (slices[slices.length - 1].offense !== ParkerRegistrationOffense.NoOffense) {
                end = slices[slices.length - 2].timeRange.endTime;
              }
              actualMaxDuration = end.diff(start).toMillis() / 60000;
            }
          }
          maxDuration = Math.min(actualMaxDuration, maxDuration);
        }
        this.maxDuration = Math.min(maxDuration, this.selectedSubcsription.maxParkingDurationInMin ?? Number.MAX_SAFE_INTEGER);
      }
    }
  }

  private assignFormFieldSubscriptions() {
    this.formSubscriptions?.unsubscribe();
    this.formSubscriptions = new Subscription();
    this.parkingHelper.assignFormFieldSubscriptions(this.parkingForm, this.formSubscriptions, this.user);
    this.formSubscriptions.add(
      combineLatest([
        this.parkingForm.controls.subscriptionAssignmentId.valueChanges.pipe(
          startWith(this.parkingForm.controls.subscriptionAssignmentId.value),
          filter(() => !!this.parkingForm.getRawValue().zoneId),
          tap(subscriptionId => {
            const zone = this.parkingProcessZoneInfoDTO?.find(z => z.id === this.parkingForm.getRawValue().zoneId);
            const selectedSubscriptionAssignment = zone?.subscriptionAssignments.find(subscription => subscription.id === subscriptionId);
            this.selectedSubcsription = selectedSubscriptionAssignment?.subscription;
            this.tariffs = this.selectedSubcsription?.tariffs
              ?? [];
            const nowUtc = DateTime.now().startOf('day').toUTC();
            this.timeGrid = new ParkingTimeGrid(nowUtc, nowUtc.plus({days: 7}));
            this.timeGrid.setup(this.tariffs, selectedSubscriptionAssignment?.validTo?.toUTC(),
                !this.selectedSubcsription?.denyForeignParkingSubscriptionFallback && zone?.foreignParkersAllowed ?
                zone?.foreignParkingSubscriptionInfos?.find(f => f.subscription.id === zone.defaultForeignParkingSubscriptionId)?.subscription?.tariffs : null);
            let validDaysFlag = 0;
            this.timeGrid.tariffValidityList.forEach(t => {
                // eslint-disable-next-line no-bitwise
                validDaysFlag = (validDaysFlag | t.getWeekDayFlagsOfValidity());
            });
            this.possibleStartTimeRanges = [];
            this.enableDisableWeekdayButtons(this.weekdayControlNames, validDaysFlag);
          }),
        ),
        this.parkingForm.controls.weekdays.valueChanges.pipe(startWith(this.parkingForm.controls.weekdays.value)),
      ])
        .pipe(
          distinctUntilChanged(),
        )
        .subscribe(([subscriptionId, weekdays]) => {
          if (this.tariffs.length) {
            const selectedWeekdaysFlag = WeekDayFlagsHelper.daysToFlag(this.parkingForm.controls.weekdays.value);
            this.possibleStartTimeRanges = [];
            if (!selectedWeekdaysFlag) {
              return;
            }

            const validities = this.timeGrid?.tariffValidityList;
            this.possibleStartTimeRanges = [];
            if (selectedWeekdaysFlag && validities) {
              this.possibleStartTimeRanges =
                ParkingTariffValidityHelper.findPossibleTariffValidityRangesMatchingForWeekDays(validities, selectedWeekdaysFlag);
              this.addExtendedInfo(this.possibleStartTimeRanges);
            }
            this.onStartTimeChange(this.parkingForm.controls.startTime.value);
          } else {
            this.weekdayControlNames.forEach(dayControlName => {
              this.parkingForm.controls.weekdays.get(dayControlName).enable({emitEvent: false});
            });
          }
        })
    );
  }

  private enableDisableWeekdayButtons(weekdayControlNames: string[], validDays: WeekDayFlags): void {
    weekdayControlNames.forEach((dayControlName, index) => {
      if (BitHelper.isBitSet(validDays, index)) {
        this.parkingForm.controls.weekdays.get(dayControlName).enable({emitEvent: false});
      } else {
        this.parkingForm.controls.weekdays.get(dayControlName).disable({emitEvent: false});
      }
    });
  }

  private addExtendedInfo(possibleStartTimeRanges: ExtendedTariffValidityRange[]) {
    const oneDayInMillis = 24 * 60 * 60 * 1000;

    possibleStartTimeRanges?.forEach(r => {
      let endTimeStr = '';
      let possibleParkingEndTime = '';
      if (r.endTime.toMillis() > oneDayInMillis) {
        endTimeStr = '24:00';
        possibleParkingEndTime = r.endTime.plus({hours: -24}).normalize().toFormat('((+1) hh:mm)');
      } else {
        endTimeStr = r.endTime?.normalize().toFormat('hh:mm');
      }
      r.startTimeStr = r.startTime?.normalize().toFormat('hh:mm');
      r.endTimeStr = endTimeStr;
      r.possibleParkingEndTime = possibleParkingEndTime;
    });
  }
}
